import { isQuickQuote } from 'util/quoteMode'
import { resolveLiveQuoteSystem } from '../projectSections/sections/quote/hooks/createQuotePlaceholderSystem'
import {
  parseDesignFromProjectPayload,
  pickFresherQuoteStudioSystem,
  resolveQuoteStudioSystemFromDesign,
} from '../projectSections/sections/quote/hooks/quoteDesignSnapshot'

const isQuotePlaceholderName = (name) => typeof name === 'string' && /^QU-/i.test(name)

/**
 * Build a MyEnergy/checkout `pricing` object from a payment option when the
 * quote placeholder `OsSystem` has no `system.pricing` (common before full calc).
 */
export const resolvePricingFromPaymentOption = (paymentOption) => {
  if (!paymentOption) return null

  const poPricing =
    paymentOption.pricing && typeof paymentOption.pricing === 'object' ? { ...paymentOption.pricing } : {}

  const including =
    poPricing.system_price_including_tax ??
    paymentOption.system_price_including_tax ??
    paymentOption.top_line_system_price ??
    0

  const excluding = poPricing.system_price_excluding_tax ?? paymentOption.system_price_excluding_tax ?? including

  const payable =
    poPricing.system_price_payable ??
    paymentOption.net_cost_to_customer ??
    paymentOption.system_price_payable ??
    including

  if (
    including === 0 &&
    excluding === 0 &&
    payable === 0 &&
    poPricing.system_price_including_tax == null &&
    poPricing.system_price_excluding_tax == null
  ) {
    return null
  }

  return {
    ...poPricing,
    system_price_including_tax: including,
    system_price_excluding_tax: excluding,
    system_price_payable: payable,
    discount: poPricing.discount ?? paymentOption.price_adjustments?.total ?? 0,
    tax: poPricing.tax ?? Math.max(0, including - excluding),
    incentive_to_installer: poPricing.incentive_to_installer ?? paymentOption.incentive_to_installer,
  }
}

/**
 * Pricing for checkout / quotation tables — prefers live `selectedSystem.pricing`,
 * then the selected payment option (non-solar quote placeholder).
 */
export const resolveProposalCheckoutPricing = ({ selectedSystem, selectedPaymentOption }) => {
  const fromSystem = selectedSystem?.pricing
  const systemHasPricing =
    fromSystem &&
    (fromSystem.system_price_including_tax != null ||
      fromSystem.system_price_excluding_tax != null ||
      fromSystem.system_price_payable != null)

  const fromPaymentOption = resolvePricingFromPaymentOption(selectedPaymentOption)

  if (systemHasPricing) {
    return { ...fromPaymentOption, ...fromSystem }
  }
  if (fromPaymentOption) {
    return fromPaymentOption
  }

  return {
    system_price_including_tax: 0,
    system_price_excluding_tax: 0,
    system_price_payable: 0,
    discount: 0,
    tax: 0,
  }
}

export const resolveProposalCheckoutSystemSize = (selectedSystem) => {
  const kw = selectedSystem?.kw_stc ?? selectedSystem?.kw
  return kw != null && Number.isFinite(Number(kw)) && Number(kw) > 0 ? Number(kw) : 0
}

const findQuoteSystemUuidInDesign = (designJson) => {
  const children = designJson?.object?.children
  if (!Array.isArray(children)) return null
  const named = children.find((c) => c?.type === 'OsSystem' && isQuotePlaceholderName(c?.name))
  if (named?.uuid) return named.uuid
  const first = children.find((c) => c?.type === 'OsSystem')
  return first?.uuid ?? null
}

const readQuoteLinesFromStudioSnapshot = (sys) => {
  if (!sys) return []
  const ud = sys.userData && typeof sys.userData === 'object' ? sys.userData : {}
  const wire = ud.quote_line_items
  if (Array.isArray(wire?.line_items) && wire.line_items.length) return wire.line_items
  if (Array.isArray(ud.line_items) && ud.line_items.length) return ud.line_items
  return []
}

/** True when the snapshot has at least one priced, non-placeholder quote row. */
const hasMeaningfulQuoteLineItems = (sys) => {
  const lines = readQuoteLinesFromStudioSnapshot(sys)
  return lines.some((item) => {
    if (!item || typeof item !== 'object') return false
    const title = String(item.title ?? item.label ?? '').trim()
    const quantity = item.quantity ?? 1
    const unit = item.unit_price ?? item.amount ?? item.value ?? 0
    const total = item.total_price ?? item.subtotal ?? Number(unit) * Number(quantity)
    return title.length > 0 && title !== 'Line item' && Number(total) > 0
  })
}

/**
 * Non-solar quotes keep the placeholder `OsSystem` on `project.design`, not in
 * `proposal_data.systems` until a full calc populates MyEnergy. Proposal V2 iframes
 * still need `selectedSystem.payment_options` and line items — resolve from design
 * (and the live Studio editor when the Pro is on the project form).
 */
export function resolveQuoteSystemForProposal({ selectedProject }) {
  if (!selectedProject) return null

  const fromEditor = resolveLiveQuoteSystem()
  let studioSystem
  if (isQuickQuote(selectedProject?.quote_mode)) {
    // Non-solar: the live Studio editor is the canonical source the Quote tab edits. Persisted
    // ``project.design`` is the fallback when the editor is missing or lags after save (common on
    // proposal navigate in CI). Only prefer design line items when the editor snapshot has none —
    // avoids resurrecting rows the Pro deleted on the Quote tab before save.
    const designJson = parseDesignFromProjectPayload(selectedProject)
    const preferredUuid = fromEditor?.uuid ?? findQuoteSystemUuidInDesign(designJson)
    const fromDesign = designJson ? resolveQuoteStudioSystemFromDesign(designJson, preferredUuid) : null

    if (!fromEditor?.uuid) {
      studioSystem = fromDesign
    } else if (fromDesign?.uuid && !hasMeaningfulQuoteLineItems(fromEditor)) {
      // Line items: persisted design when editor lagged after save. Payment options: pickFresher
      // when both sides have no rows yet (new quote before first adder — design may already have Cash).
      studioSystem = hasMeaningfulQuoteLineItems(fromDesign)
        ? fromDesign
        : pickFresherQuoteStudioSystem(fromEditor, fromDesign)
    } else {
      studioSystem = fromEditor
    }
  } else {
    // Solar (original baseline): merge editor and design snapshots by freshness rather than
    // preferring the editor outright.
    const designJson = parseDesignFromProjectPayload(selectedProject)
    const preferredUuid = fromEditor?.uuid ?? findQuoteSystemUuidInDesign(designJson)
    const fromDesign = designJson ? resolveQuoteStudioSystemFromDesign(designJson, preferredUuid) : null
    studioSystem = pickFresherQuoteStudioSystem(fromEditor, fromDesign)
  }
  if (!studioSystem?.uuid) return null

  const userData =
    studioSystem.userData && typeof studioSystem.userData === 'object' ? { ...studioSystem.userData } : {}

  if (!userData.quote_line_items) {
    const lineItems = userData.line_items
    const incentives = userData.incentive_to_installer
    if ((Array.isArray(lineItems) && lineItems.length > 0) || (Array.isArray(incentives) && incentives.length > 0)) {
      userData.quote_line_items = {
        line_items: Array.isArray(lineItems) ? lineItems : [],
        incentives: Array.isArray(incentives) ? incentives : [],
      }
    }
  }

  const paymentOptions = studioSystem.payment_options || userData.payment_options || []
  const pricing = studioSystem.pricing ?? resolvePricingFromPaymentOption(paymentOptions[0]) ?? undefined

  return {
    uuid: studioSystem.uuid,
    name: studioSystem.name,
    payment_options: paymentOptions,
    userData,
    line_items: studioSystem.line_items || userData.line_items || [],
    pricing,
    kw_stc: studioSystem.kw_stc ?? 0,
    show_customer: true,
    order: 0,
    id: userData.id,
  }
}
